import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { User } from "./types";

interface SubscriptionDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

import { useTranslation } from "react-i18next";

export function SubscriptionDialog({ user, open, onOpenChange, onSuccess }: SubscriptionDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tier, setTier] = useState<string>("free");
  const [status, setStatus] = useState<string>("active");
  const [duration, setDuration] = useState<string>("0");

  // Load initial values when user changes
  useEffect(() => {
    if (user && open) {
      setTier(user.subscription_tier || "free");
      setStatus(user.subscription_status || "active");
      setDuration("0"); // Reset duration on open
    }
  }, [user, open]);

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // 1. Update Profile (for record keeping and admin view)
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          subscription_tier: tier,
          subscription_status: status,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      // 2. If duration is set, update Subscriptions table (for access control)
      //    We only do this if a specific duration is chosen to ensure functional expiry.
      if (duration !== "0") {
        const days = parseInt(duration);
        const now = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + days);

        // Get current admin user ID for gift_granted_by
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        const { error: subError } = await supabase
          .from("subscriptions")
          .upsert({
            user_id: user.id,
            status: status,
            plan: tier,
            current_period_start: now.toISOString(),
            current_period_end: endDate.toISOString(),
            // Gift tracking fields for financial management
            gift_granted_at: status === 'gifted' ? now.toISOString() : null,
            gift_duration_days: status === 'gifted' ? days : null,
            gift_granted_by: status === 'gifted' ? currentUser?.id : null,
            updated_at: now.toISOString(),
          }, { onConflict: 'user_id' });

        if (subError) throw subError;
      }

      toast.success(t('admin.users.subscriptionDialog.success'));
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error(t('admin.users.subscriptionDialog.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="border-2 border-black bg-[#F5F0E8] p-8 shadow-[8px_8px_0_#1A1A1A] sm:max-w-[500px] sm:rounded-xl"
        aria-describedby="subscription-dialog-description"
      >
        <DialogHeader className="mb-6">
          <DialogTitle className="text-2xl font-black tracking-tight">{t('admin.users.subscriptionDialog.title')}</DialogTitle>
          <DialogDescription id="subscription-dialog-description" className="sr-only">
            {t('admin.users.subscriptionDialog.desc')}
          </DialogDescription>
        </DialogHeader>
        
        {user && (
          <div className="space-y-6">
            {/* User Info Block */}
            <div>
              <label className="mb-1 block text-sm font-bold text-gray-600">{t('admin.users.subscriptionDialog.user')}</label>
              <div className="text-lg font-black text-[#1A1A1A]">{user.name || t('admin.users.unnamed')}</div>
              <div className="font-medium text-gray-500">{user.email}</div>
            </div>

            {/* Controls */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                 <label htmlFor="tier" className="mb-1 block text-sm font-bold text-gray-600">{t('admin.users.subscriptionDialog.tier')}</label>
                 <select
                   id="tier"
                   value={tier}
                   onChange={(e) => setTier(e.target.value)}
                   className="h-14 w-full appearance-none rounded-lg border-2 border-black bg-white px-4 text-lg font-black text-[#1A1A1A] transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black"
                 >
                   <option value="free">{t('admin.users.subscriptionDialog.tiers.free')}</option>
                   <option value="basic">{t('admin.users.subscriptionDialog.tiers.basic')}</option>
                   <option value="pro">{t('admin.users.subscriptionDialog.tiers.pro')}</option>
                 </select>
              </div>
              
              <div>
                 <label htmlFor="status" className="mb-1 block text-sm font-bold text-gray-600">{t('admin.users.subscriptionDialog.status')}</label>
                 <select
                   id="status"
                   value={status}
                   onChange={(e) => setStatus(e.target.value)}
                   className="h-14 w-full appearance-none rounded-lg border-2 border-black bg-white px-4 text-lg font-black text-[#1A1A1A] transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black"
                 >
                   <option value="active">{t('admin.users.subscriptionDialog.statuses.active')}</option>
                   <option value="trialing">{t('admin.users.subscriptionDialog.statuses.trialing')}</option>
                   <option value="gifted">{t('admin.users.subscriptionDialog.statuses.gifted')}</option>
                   <option value="canceled">{t('admin.users.subscriptionDialog.statuses.canceled')}</option>
                   <option value="past_due">{t('admin.users.subscriptionDialog.statuses.past_due')}</option>
                   <option value="suspended">{t('admin.users.subscriptionDialog.statuses.suspended')}</option>
                 </select>
              </div>
            </div>

            {/* Duration Control */}
            <div>
                <label htmlFor="duration" className="mb-1 block text-sm font-bold text-gray-600">{t('admin.users.subscriptionDialog.duration')}</label>
                <select
                  id="duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="h-14 w-full appearance-none rounded-lg border-2 border-black bg-white px-4 text-lg font-black text-[#1A1A1A] transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <option value="0">{t('admin.users.subscriptionDialog.durations.0')}</option>
                  <option value="3">{t('admin.users.subscriptionDialog.durations.3')}</option>
                  <option value="7">{t('admin.users.subscriptionDialog.durations.7')}</option>
                  <option value="30">{t('admin.users.subscriptionDialog.durations.30')}</option>
                  <option value="90">{t('admin.users.subscriptionDialog.durations.90')}</option>
                  <option value="180">{t('admin.users.subscriptionDialog.durations.180')}</option>
                  <option value="365">{t('admin.users.subscriptionDialog.durations.365')}</option>
                </select>
            </div>
            
             {/* Tip Box */}
             <div className="rounded-lg border-2 border-black bg-[#FFD93D] p-4">
                <p className="text-sm font-bold leading-relaxed text-[#1A1A1A]">
                  💡 {t('admin.users.subscriptionDialog.tip')}
                </p>
             </div>
          </div>
        )}

        <DialogFooter className="mt-8 flex items-center justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="px-6 py-2 bg-white border-2 border-black rounded-full hover:bg-gray-50 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all text-sm font-bold text-[#1A1A1A]"
            disabled={loading}
          >
            {t('admin.users.passwordDialog.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-[#6FEDD6] border-2 border-black rounded-full hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all text-sm font-bold text-[#1A1A1A] disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('admin.users.subscriptionDialog.save')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
